/* src/components/customGame/setup/panels/stimuli/ShapeStimulusPanel.js */
import React from 'react';
import PropTypes from 'prop-types';

// Re-using the SubSettingsSection helper
const SubSettingsSection = ({ title, children, helpText = "" }) => (
    <div className="mb-4 pt-3">
        <label className="block text-sm font-semibold text-gray-700 mb-1">{title}</label>
        {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}
        <div className="space-y-3">
            {children}
        </div>
    </div>
);

SubSettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
};

const PREDEFINED_SHAPE_SETS = {
    "Geometric (Simple)": ["Square", "Circle", "Triangle"],
    "Geometric (Extended)": ["Square", "Circle", "Triangle", "Star", "Hexagon", "Diamond"],
    "Basic Arrows": ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"],
    // "Thematic Icons (Weather)": ["Sun", "Cloud", "Rain", "Snowflake"], // Example for future
};


const ShapeStimulusPanel = ({ config, onConfigChange, pathPrefix, globalStimulusConfig }) => {
    const currentConfig = config || {};

    const handleChange = (key, value) => {
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    const handleSetTypeChange = (newSetType) => {
        handleChange('SetType', newSetType);
        if (PREDEFINED_SHAPE_SETS[newSetType]) {
            handleChange('ShapeList', PREDEFINED_SHAPE_SETS[newSetType]);
        } else if (newSetType === "Custom List") {
            // Initialize or keep existing custom list
            if (!Array.isArray(currentConfig.ShapeList) || PREDEFINED_SHAPE_SETS[currentConfig.SetType]) { // If previous was predefined
                handleChange('ShapeList', ["CustomShape1", "CustomShape2"]);
            }
        }
        // "Custom Images" type would require different handling for ShapeList (e.g. file upload UI)
    };

    // For Custom List, manage shapes as a comma-separated string for now
    const handleCustomShapeListChange = (e) => {
        const shapeString = e.target.value;
        const shapesArray = shapeString.split(',')
            .map(shape => shape.trim())
            .filter(shape => shape.length > 0);
        handleChange('ShapeListUserString', shapeString); // Store the raw string
        handleChange('ShapeList', shapesArray); // Store the parsed array
    };
    
    // Simple preview for shapes (would need actual rendering components later)
    const renderShapePreview = (shapeName) => {
        // This is highly simplified. In a real app, you'd have SVG components or images.
        let style = {
            width: '24px', height: '24px', display: 'inline-block', margin: '2px',
            border: '1px solid #ccc', textAlign: 'center', lineHeight: '22px', fontSize: '10px',
            backgroundColor: currentConfig.FillStyle === 'Filled' ? (currentConfig.Color || '#4F46E5') : 'transparent',
            borderColor: currentConfig.FillStyle === 'Outline' ? (currentConfig.Color || '#4F46E5') : '#ccc',
            color: currentConfig.FillStyle === 'Outline' ? (currentConfig.Color || '#4F46E5') : '#fff', // Text color for filled might need contrast
        };
        if (currentConfig.FillStyle === 'Outline') {
            style.borderWidth = `${currentConfig.LineThickness || 2}px`;
        }

        switch (shapeName?.toLowerCase()) {
            case 'square': return <div style={{...style, borderRadius: '0%'}} title={shapeName}>Sq</div>;
            case 'circle': return <div style={{...style, borderRadius: '50%'}} title={shapeName}>Ci</div>;
            case 'triangle': return <div style={{...style, width: 0, height: 0, borderLeft: '12px solid transparent', borderRight: '12px solid transparent', borderBottom: `20px solid ${style.backgroundColor}`, backgroundColor: 'transparent', borderTopColor: 'transparent', borderBottomColor: currentConfig.Color || '#4F46E5'}} title={shapeName}></div>;
            default: return <div style={style} title={shapeName}>{shapeName.substring(0,2)}</div>;
        }
    };


    return (
        <div className="space-y-4">
            <SubSettingsSection title="Shape Set Definition" helpText="Choose the type and list of shapes.">
                <div>
                    <label htmlFor={`${pathPrefix}-SetType`} className="block text-xs font-medium text-gray-600 mb-1">
                        Shape Set Type
                    </label>
                    <select
                        id={`${pathPrefix}-SetType`}
                        name="SetType"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.SetType || 'Geometric (Simple)'}
                        onChange={(e) => handleSetTypeChange(e.target.value)}
                    >
                        {Object.keys(PREDEFINED_SHAPE_SETS).map(name => (
                             <option key={name} value={name}>{name}</option>
                        ))}
                        <option value="Custom List">Custom List (Names)</option>
                        {/* <option value="Custom Images">Custom Images (Upload)</option> */}
                    </select>
                </div>

                {currentConfig.SetType === 'Custom List' && (
                    <div>
                        <label htmlFor={`${pathPrefix}-ShapeListUserString`} className="block text-xs font-medium text-gray-600 mb-1">
                            Custom Shape Names (comma-separated, e.g., ShapeA,ShapeB)
                        </label>
                        <textarea
                            id={`${pathPrefix}-ShapeListUserString`}
                            name="ShapeListUserString"
                            rows="2"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={currentConfig.ShapeListUserString || (Array.isArray(currentConfig.ShapeList) ? currentConfig.ShapeList.join(',') : '')}
                            onChange={handleCustomShapeListChange}
                            placeholder="ShapeA,ShapeB,AnotherShape"
                        />
                         <p className="text-xs text-gray-500 mt-1">
                            Visual representation for custom names will depend on your rendering logic.
                            {currentConfig.ShapeList && ` Currently ${currentConfig.ShapeList.length} shapes defined.`}
                        </p>
                    </div>
                )}
                 {currentConfig.SetType === 'Custom Images' && (
                     <p className="mt-1 text-sm text-gray-500 p-3 bg-gray-100 rounded-md">
                        Image upload and management for custom shapes will be available in a future update.
                    </p>
                 )}

                {Array.isArray(currentConfig.ShapeList) && currentConfig.ShapeList.length > 0 && (
                    <div className="mt-2">
                        <p className="block text-xs font-medium text-gray-600 mb-1">Current Shape Set Preview:</p>
                        <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded bg-gray-50">
                            {currentConfig.ShapeList.map((shapeName, index) => (
                                <div key={index} className="flex flex-col items-center text-xs">
                                    {renderShapePreview(shapeName)}
                                    <span className="mt-1 text-gray-600 truncate max-w-[60px]">{shapeName}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </SubSettingsSection>

            <SubSettingsSection title="Shape Visual Properties" helpText="Customize how shapes appear.">
                <div>
                    <label htmlFor={`${pathPrefix}-FillStyle`} className="block text-xs font-medium text-gray-600 mb-1">
                        Fill Style
                    </label>
                    <select
                        id={`${pathPrefix}-FillStyle`}
                        name="FillStyle"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.FillStyle || 'Filled'}
                        onChange={(e) => handleChange('FillStyle', e.target.value)}
                    >
                        <option value="Filled">Filled</option>
                        <option value="Outline">Outline</option>
                    </select>
                </div>

                {currentConfig.FillStyle === 'Outline' && (
                    <div>
                        <label htmlFor={`${pathPrefix}-LineThickness`} className="block text-xs font-medium text-gray-600 mb-1">
                            Outline Thickness (pixels)
                        </label>
                        <input
                            type="number"
                            id={`${pathPrefix}-LineThickness`}
                            name="LineThickness"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={currentConfig.LineThickness || 2}
                            onChange={(e) => handleChange('LineThickness', parseInt(e.target.value, 10))}
                            min="1"
                            max="10"
                        />
                    </div>
                )}

                <div>
                    <label htmlFor={`${pathPrefix}-Color`} className="block text-xs font-medium text-gray-600 mb-1">
                        Shape Color (Fill or Outline)
                    </label>
                    <input
                        type="color"
                        id={`${pathPrefix}-Color`}
                        name="Color"
                        className="mt-1 block w-full h-10 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={currentConfig.Color || '#4F46E5'} // Default indigo
                        onChange={(e) => handleChange('Color', e.target.value)}
                    />
                    {globalStimulusConfig?.EnabledTypes?.includes('Color') && (
                        <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 p-2 rounded-md">
                            Warning: The "Color" stimulus modality is also active. Ensure this shape color does not conflict or is managed intentionally.
                        </p>
                    )}
                </div>
            </SubSettingsSection>

            {/* Placeholders for Advanced Shape Properties (Phase 2/3) */}
            {/* <SubSettingsSection title="Advanced Shape Properties" helpText="Fine-tune shape characteristics.">
                <div>
                    <label htmlFor={`${pathPrefix}-ComplexityControl`} className="block text-xs font-medium text-gray-600 mb-1">
                        Complexity Control (0.0 to 1.0 or Low/Medium/High)
                    </label>
                    <input type="text" value={currentConfig.ComplexityControl || 0.5} disabled className="mt-1 block w-full bg-gray-100 ..."/>
                    <p className="text-xs text-gray-500 mt-1">Control for generated shapes or selecting variants. (Future)</p>
                </div>
                 <div className="flex items-center">
                    <input
                        id={`${pathPrefix}-RotationInvariance`}
                        name="RotationInvariance"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        checked={currentConfig.RotationInvariance !== undefined ? currentConfig.RotationInvariance : true}
                        onChange={(e) => handleChange('RotationInvariance', e.target.checked)}
                    />
                    <label htmlFor={`${pathPrefix}-RotationInvariance`} className="ml-2 block text-sm text-gray-700">
                        Rotation Invariance (Rotated shapes are considered a match)
                    </label>
                </div>
                <div>
                    <label htmlFor={`${pathPrefix}-SizeVariation`} className="block text-xs font-medium text-gray-600 mb-1">
                        Size Variation
                    </label>
                     <select
                        id={`${pathPrefix}-SizeVariation`}
                        name="SizeVariation"
                        className="mt-1 block w-full bg-gray-100 ..."
                        value={currentConfig.SizeVariation || 'Fixed Size'}
                        disabled
                    >
                        <option value="Fixed Size">Fixed Size</option>
                        <option value="Minor Variation">Minor Variation (Still Matches)</option>
                        <option value="Match Size Required">Match Size Required (Different sizes don't match)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Control over size changes and match criteria. (Future)</p>
                </div>
            </SubSettingsSection> */}

        </div>
    );
};

ShapeStimulusPanel.propTypes = {
    config: PropTypes.shape({
        SetType: PropTypes.string,
        ShapeList: PropTypes.arrayOf(PropTypes.string),
        ShapeListUserString: PropTypes.string,
        ComplexityControl: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        RotationInvariance: PropTypes.bool,
        SizeVariation: PropTypes.string,
        FillStyle: PropTypes.string,
        LineThickness: PropTypes.number,
        Color: PropTypes.string,
    }),
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Stimulus.Shape"
    globalStimulusConfig: PropTypes.object,
};

export default ShapeStimulusPanel;